import { isValid } from 'date-fns';
import {
  BaseDocument,
  DocumentModuleOperation,
  DocumentModuleSignatory,
} from '../../model';
import { getKidUrl } from '../../utils/api-utils';
import {
  DocumentBeanComplexMetadata,
  OrderMetadataBean,
} from '../generated/papyrus-metadata/api';
import { DispositionMetadataBean } from '../generated/papyrus-stream';
import { BundleState } from '../generated/signroom-orchestrator';
import { parseServerDateTime } from './commons-api-parsers';

export const parseBaseDocument = <T = string>(
  item: DocumentBeanComplexMetadata,
  details?: BundleState
): BaseDocument<T> => {
  return {
    id: item.id,
    flagIsRead: item.read,
    fileUrl: item.documentURL,
    title: item.documentName,
    bundleId: details?.bundleId,
    uploadDate: item.creationDate? parseServerDateTime(item.creationDate):undefined,
    category: item.category,
    code: item.documentType as unknown as T,
  };
};

export const parseModuleInformation = (
  item: DocumentBeanComplexMetadata,
  details: BundleState,
  complexMetadata: DispositionMetadataBean
) => {
  const [document, dispositionRTO,documentNumber] = item.documentName?.split(/(_\d*[_.])/) || [];
  let expiryDate: Date | undefined;
  if (item.expirationDate && isValid(parseServerDateTime(item.expirationDate))) {
    expiryDate = parseServerDateTime(item.expirationDate);
  } else if (details.expirationDate && isValid(new Date(details.expirationDate))) {
    expiryDate = new Date(details.expirationDate);
  }
  return [
    {
      document,
      documentNumber: complexMetadata.planId || documentNumber?.split(".")[0],
      expiryDate,
      dispositionRTO: dispositionRTO?.replaceAll(/[_.]/g, ''),
    },
  ];
};

const mapOrder = (order: OrderMetadataBean): DocumentModuleOperation => {
  return {
    mandate: order.mandate || '',
    operation: order.operationDesc || order.operation || '',
    company: order.productCompanyDesc || order.productCompany || '',
    paymentType: order.paymentType || '',
    product: order.productName || '',
    kidUrl: order.existsKidDoc === true ? getKidUrl(order.productIsin || order.productCode) : undefined,
    import: isNaN(parseFloat(order.amount))
      ? undefined
      : {
          value: parseFloat(order.amount),
          currency: order.currency || '',
        },
  };
};

export const parseModuleOperation = (
  complexMetadata: DispositionMetadataBean
): DocumentModuleOperation[] => complexMetadata.orders?.map(mapOrder) || [];

export const parseModuleSignatory = (
  details: BundleState
): DocumentModuleSignatory[] => {
  const financialAdvisor = details.recipients?.filter(
    (item) => item.userType === 'A'
  )?.[0]?.recipientFullName || '';

  const customers: string = (details.recipients || [])
    .filter((item) => item.userType === 'C')
    .map((customer) => customer.recipientFullName)
    .join(',');

  return [
    {
      signatory: customers,
      financialAdvisor,
    },
  ];
};

export const parsePrincipalModuleOperation = (
  documents: DocumentBeanComplexMetadata[]
) =>
  documents
    .filter(
      (item) =>
        item.documentType !== 'REPORT_PROPOSTA' && item.documentType !== 'KIID'
    )
    .flatMap((item) =>
      (item.complexMetadata as DispositionMetadataBean).orders?.map(mapOrder) || []
    );

export const getModuleOperation = (
  item: DocumentBeanComplexMetadata,
  complexMetadata: DispositionMetadataBean,
  documents: DocumentBeanComplexMetadata[]
): DocumentModuleOperation[] => {
  return item.documentType === 'REPORT_PROPOSTA'
    ? parsePrincipalModuleOperation(documents)
    : parseModuleOperation(complexMetadata);
};
